import type { StatusMeta } from "@/lib/adminContent";

const meta = (label: string, tone: StatusMeta["tone"]): StatusMeta => ({ label, tone });

export function metaFor(
  map: Record<string, StatusMeta>,
  key: string | null | undefined,
): StatusMeta {
  if (!key) return { label: "—", tone: "neutral" };
  return map[key] || { label: key, tone: "neutral" };
}

/* ------------------------------ Visitor Groups ------------------------------ */
export interface VisitorGroup {
  id: number;
  name: string;
  group_type: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  size: number;
  location: string | null;
  total_visits: number;
  total_bookings: number;
  total_spend: number;
  status: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export const GROUP_TYPE: Record<string, StatusMeta> = {
  school: meta("School", "accent"),
  college: meta("College", "accent"),
  corporate: meta("Corporate", "primary"),
  family: meta("Family", "secondary"),
  group: meta("Group", "secondary"),
  other: meta("Other", "neutral"),
};

export const GROUP_STATUS: Record<string, StatusMeta> = {
  active: meta("Active", "primary"),
  inactive: meta("Inactive", "neutral"),
};

/* -------------------------------- Complaints -------------------------------- */
export interface Complaint {
  id: number;
  ref: string | null;
  subject: string;
  visitor_name: string | null;
  visitor_email: string | null;
  category: string | null;
  against: string | null;
  priority: string;
  status: string;
  description: string | null;
  resolution: string | null;
  raised_at: string | null;
  created_at: string;
  updated_at: string;
}

export const COMPLAINT_CATEGORY = ["Service", "Food", "Safety", "Billing", "Host Conduct", "Transport", "Accessibility", "Other"];
export const COMPLAINT_PRIORITY: Record<string, StatusMeta> = {
  high: meta("High", "accent"),
  medium: meta("Medium", "secondary"),
  low: meta("Low", "neutral"),
};
export const COMPLAINT_STATUS: Record<string, StatusMeta> = {
  open: meta("Open", "accent"),
  in_progress: meta("In Progress", "secondary"),
  resolved: meta("Resolved", "primary"),
  closed: meta("Closed", "neutral"),
};

/* -------------------------------- Incidents --------------------------------- */
export interface Incident {
  id: number;
  ref: string | null;
  title: string;
  farm_name: string | null;
  incident_type: string;
  severity: string;
  status: string;
  reported_by: string | null;
  occurred_at: string | null;
  description: string | null;
  action_taken: string | null;
  created_at: string;
  updated_at: string;
}

export const INCIDENT_TYPE: Record<string, StatusMeta> = {
  minor: meta("Minor", "secondary"),
  near_miss: meta("Near Miss", "secondary"),
  medical: meta("Medical", "accent"),
  environmental: meta("Environmental", "accent"),
  property: meta("Property", "accent"),
  major: meta("Major", "danger"),
};

export const INCIDENT_SEVERITY: Record<string, StatusMeta> = {
  low: meta("Low", "neutral"),
  medium: meta("Medium", "secondary"),
  high: meta("High", "accent"),
  critical: meta("Critical", "danger"),
};

export const INCIDENT_STATUS: Record<string, StatusMeta> = {
  open: meta("Open", "accent"),
  in_progress: meta("Investigating", "secondary"),
  resolved: meta("Resolved", "primary"),
  closed: meta("Closed", "neutral"),
};

/* ----------------------------- Safety Protocols ----------------------------- */
export interface SafetyProtocol {
  id: number;
  title: string;
  category: string;
  description: string | null;
  applies_to: string | null;
  checklist: string[] | null;
  status: string;
  last_reviewed: string | null;
  created_at: string;
  updated_at: string;
}

export const PROTOCOL_CATEGORY = ["Orientation", "Medical", "Environment", "Animals", "Food", "Emergency", "General"];
export const PROTOCOL_APPLIES = ["All farms", "Konaseema farms", "Farms with animals", "Farms serving meals", "Farm kitchen network"];
export const PROTOCOL_STATUS: Record<string, StatusMeta> = {
  active: meta("Active", "primary"),
  draft: meta("Draft", "neutral"),
  archived: meta("Archived", "neutral"),
};

/* --------------------------- Compliance Documents --------------------------- */
export interface ComplianceDocument {
  id: number;
  title: string;
  doc_type: string;
  category: string;
  related_to: string | null;
  issued_by: string | null;
  issued_at: string | null;
  expires_at: string | null;
  status: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export const DOC_TYPE = ["Certificate", "Licence", "Insurance", "Permit", "Report", "Policy"];
export const DOC_CATEGORY = ["Legal", "Tax", "Licensing", "Food", "Insurance", "Safety", "Environment"];
export const DOC_STATUS: Record<string, StatusMeta> = {
  valid: meta("Valid", "primary"),
  expiring: meta("Expiring Soon", "accent"),
  expired: meta("Expired", "danger"),
  pending: meta("Pending", "secondary"),
};

/* ---------------------------- Compliance Items ------------------------------ */
export interface ComplianceItem {
  id: number;
  requirement: string;
  category: string;
  authority: string | null;
  applies_to: string | null;
  status: string;
  due_date: string | null;
  owner: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export const COMPLIANCE_CATEGORY = ["Licensing", "Tax", "Food", "Insurance", "Safety", "Environment", "Labour"];
export const COMPLIANCE_STATUS: Record<string, StatusMeta> = {
  compliant: meta("Compliant", "primary"),
  in_progress: meta("In Progress", "secondary"),
  pending: meta("Pending", "accent"),
  overdue: meta("Overdue", "danger"),
};

/* ------------------------------- Admin Users -------------------------------- */
export interface AdminUser {
  id: number;
  name: string;
  email: string;
  role: string;
  phone: string | null;
  status: string;
  last_login: string | null;
  created_at: string;
  updated_at: string;
}

export const ADMIN_USER_STATUS: Record<string, StatusMeta> = {
  active: meta("Active", "primary"),
  invited: meta("Invited", "secondary"),
  suspended: meta("Suspended", "neutral"),
};

/* ------------------------------- Admin Roles -------------------------------- */
export interface AdminRole {
  id: number;
  name: string;
  description: string | null;
  permissions: string[] | null;
  users_count: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export const ROLE_STATUS: Record<string, StatusMeta> = {
  active: meta("Active", "primary"),
  archived: meta("Archived", "neutral"),
};

/* ------------------------------- Audit Logs --------------------------------- */
export interface AuditLog {
  id: number;
  actor: string | null;
  action: string;
  entity: string | null;
  entity_id: string | null;
  detail: string | null;
  ip: string | null;
  created_at: string;
}

export const AUDIT_ACTION: Record<string, StatusMeta> = {
  created: meta("Created", "primary"),
  updated: meta("Updated", "secondary"),
  deleted: meta("Deleted", "danger"),
  approved: meta("Approved", "primary"),
  rejected: meta("Rejected", "neutral"),
  published: meta("Published", "primary"),
  processed: meta("Processed", "secondary"),
  refunded: meta("Refunded", "accent"),
};

/* ------------------------------ Integrations -------------------------------- */
export interface Integration {
  id: number;
  name: string;
  category: string;
  description: string | null;
  status: string;
  account: string | null;
  last_sync: string | null;
  created_at: string;
  updated_at: string;
}

export const INTEGRATION_STATUS: Record<string, StatusMeta> = {
  connected: meta("Connected", "primary"),
  available: meta("Available", "secondary"),
  disabled: meta("Disabled", "neutral"),
  error: meta("Error", "danger"),
};

export const INTEGRATION_CATEGORY = ["Payments", "Messaging", "Email", "Marketing", "Analytics", "Location", "Other"];

/* ----------------------------- System Settings ------------------------------ */
export interface SystemSetting {
  id: number;
  key: string;
  label: string;
  group_name: string;
  value: string | null;
  value_type: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export const SETTING_GROUPS = ["General", "Commerce", "Operations", "Notifications"];
export const SETTING_TYPES = ["text", "number", "boolean", "json"];